import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { ProductCarousel, IProductCarousel } from '../product-carousel/ProductCarousel';
import { ProductBriefCollectionWrapper } from '../product-brief-collection/ProductBriefCollectionWrapper';
import { IProductBriefCollection } from '../product-brief-collection/ProductBriefCollection';
import { ICTAConfig } from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { ContentSpacing, Breakpoints } from '@estee/elc-base-theme';
import { Heading4 } from '@estee/elc-typography';
import { classes } from '../../../internal/utils/Classes';
import { ProductSortingDropdown } from '../../../internal/views/product-grid/ProductSortingDropdown';
import { FilterSet } from '../../domain/entities/FilterSet';
import { Desktop, Mobile } from '@estee/elc-layouts';
import { configConsumer } from '@estee/elc-service';
import { IFiltersConfig } from '../../interfaces/IFilters';
import { getResponsiveConfig } from '../../utils/ConfigUtils';
import { OrderableFilters } from '../product-filters/orderable-filters/OrderableFilters';
import { GridActionsBarWrapper, IGridActionsBarConfig } from './GridHeaderBarWrapper';
import { Filter } from '~domain/entities/Filter';
import { ItemAboveViewport } from '../item-above-viewport/ItemAboveViewport';

export enum Formatter {
    DEFAULT = 'default',
    CAROUSEL = 'carousel'
}

interface IProductGridConfig {
    gridCTA: ICTAConfig;
    gridActionsBar: IGridActionsBarConfig;
}

interface IProductCTA {
    backgroundColor?: string;
    borderColor?: string;
}

interface IProductCTAText {
    textColor?: string;
}

export interface IProductGridProps {
    formatter: Formatter;
    carouselProps: IProductCarousel['config'];
    briefProps: IProductBriefCollection;
    productSortingDropdownValue: string;
    productsNumber: number;
    appliedFiltersList?: Filter[];
    appliedFilters?: FilterSet[];
    filterSets?: FilterSet[];
    isAllGiftsFilterApplied?: boolean;
    areFiltersApplied?: boolean;
    positionGridInsideAccordion?: boolean;
    totalAppliedFilters?: number;
    hideSorting?: boolean;
    isFilterDisplayStacked?: boolean;
    isFilterDisplayTabbed?: boolean;
    className?: string;
    setProductSortingDropdownValue(_: string, v: string): void;
    config?: IProductGridConfig;
    filters?: IFiltersConfig;
    scrollIntoView?(offset?: number): void;
}

export const QUICK_VIEW_SELECTOR_CLASSNAME = 'elc-quickview-renderer';

const ProductCTAWrapper = styled.div`
    margin-top: ${ContentSpacing.space24};
`;

const ProductCTA = styled.a<IProductCTA>`
    width: ${ContentSpacing.space60};
    background-color: ${(props) => (props.backgroundColor ? props.backgroundColor : '')};
    box-shadow: ${(props) => (props.borderColor ? `inset 0 0 0 1px ${props.borderColor}` : '')};
`;

const ProductCTAText = styled.span<IProductCTAText>`
    color: ${(props) => (props.textColor ? props.textColor : '')};
`;

@configConsumer(['gridCTA', 'gridActionsBar'])
@observer
class ProductGrid extends React.Component<IProductGridProps> {
    public static defaultProps: IProductGridProps;

    componentDidUpdate(oldProps: IProductGridProps) {
        if (this.props.appliedFiltersList?.length !== oldProps.appliedFiltersList?.length) {
            this.props.scrollIntoView && this.props.scrollIntoView();
        }
    }

    private getProductCTA = () => {
        const productCTAWrapperClassNames = classes('product-cta-wrapper');
        const {
            gridCTA: {
                label,
                absoluteUrl,
                url,
                newTab,
                ctaCustomStyleClassName,
                backgroundColor,
                borderColor,
                textColor
            } = {}
        } = this.props.config as IProductGridConfig;
        const productCTAClassNames = ctaCustomStyleClassName
            ? classes('custom-cta', ctaCustomStyleClassName)
            : classes('product-cta');

        let ctaURL = url;
        if (absoluteUrl && url) {
            const isAbsoluteUrlRegex = new RegExp('^(?:[a-z]+:)?//', 'i');
            const baseUrl = isAbsoluteUrlRegex.test(url) ? '' : 'https://';
            ctaURL = `${baseUrl}${url}`;
        }

        const ctaUrlTarget = newTab ? '_blank' : '';
        const ctaLabel =
            ctaCustomStyleClassName || textColor ? (
                <ProductCTAText textColor={textColor}>{label}</ProductCTAText>
            ) : (
                <Heading4>{label}</Heading4>
            );

        return (
            <ProductCTAWrapper className={productCTAWrapperClassNames}>
                <ProductCTA
                    className={productCTAClassNames}
                    href={ctaURL}
                    target={ctaUrlTarget}
                    backgroundColor={backgroundColor}
                    borderColor={borderColor}
                >
                    {ctaLabel}
                </ProductCTA>
            </ProductCTAWrapper>
        );
    };

    private getSorting(displaySortingDropdown: boolean) {
        if (!displaySortingDropdown) {
            return null;
        }

        const sortWrapperClassNames = classes('sort-wrapper');

        return (
            <ProductSortingDropdown
                className={sortWrapperClassNames}
                productSortingDropdownValue={this.props.productSortingDropdownValue}
                setProductSortingDropdownValue={this.props.setProductSortingDropdownValue}
            />
        );
    }

    private getHeaderBarContent(wrapperClassName: string, filtersConfig?: IFiltersConfig) {
        const displaySortingDropdown = !this.props.hideSorting && this.props.productsNumber > 0;
        const isFilterEnabled = filtersConfig && filtersConfig.enabled;
        const isGridAvailable = isFilterEnabled || displaySortingDropdown;

        if (!isGridAvailable) {
            return null;
        }

        return (
            <GridActionsBarWrapper className={wrapperClassName}>
                {isFilterEnabled && (
                    <OrderableFilters
                        productsNumber={this.props.productsNumber}
                        filterSets={this.props.filterSets || []}
                        appliedFilters={this.props.appliedFilters || []}
                        totalAppliedFilters={this.props.totalAppliedFilters}
                        filtersConfig={filtersConfig}
                        scrollToGridTop={this.props.scrollIntoView}
                    />
                )}

                {this.getSorting(displaySortingDropdown)}
            </GridActionsBarWrapper>
        );
    }

    private getHeaderBar() {
        const headerBarClassNames = classes('product-grid--header');
        const headerBarMobileClassNames = classes(
            'product-grid--header',
            'product-grid--header-mobile'
        );

        const { filters } = this.props;
        let desktopConfig: IFiltersConfig | undefined;
        let mobileConfig: IFiltersConfig | undefined;
        if (filters) {
            const { componentElementsOrderConfig, responsive, ...config } = filters;
            desktopConfig = {
                ...config,
                ...getResponsiveConfig(Breakpoints.desktop, responsive),
                componentElementsOrderConfig
            };
            mobileConfig = {
                ...config,
                componentElementsOrderConfig
            };
        }

        return (
            <>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() => this.getHeaderBarContent(headerBarClassNames, desktopConfig)}
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => this.getHeaderBarContent(headerBarMobileClassNames, mobileConfig)}
                </Mobile>
            </>
        );
    }

    public render() {
        const {
            carouselProps,
            briefProps,
            isAllGiftsFilterApplied,
            areFiltersApplied,
            formatter
        } = this.props;

        const { gridCTA: { enabled: gridCTAEnabled } = {} } = this.props
            .config as IProductGridConfig;

        const productGridCTA = gridCTAEnabled && this.getProductCTA();

        if (formatter === Formatter.CAROUSEL) {
            return (
                <>
                    <ProductCarousel config={carouselProps} />
                    {productGridCTA}
                </>
            );
        }

        return (
            <ItemAboveViewport component="product-grid">
                <>
                    {this.getHeaderBar()}
                    <ProductBriefCollectionWrapper
                        isAllGiftsFilterApplied={isAllGiftsFilterApplied}
                        areFiltersApplied={areFiltersApplied}
                        {...briefProps}
                    />
                    <div className={QUICK_VIEW_SELECTOR_CLASSNAME} />
                    {productGridCTA}
                </>
            </ItemAboveViewport>
        );
    }
}
export default ProductGrid;
